import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getBackendUrl } from '../helpers/url'

// Every view name handled by the switch in server/helpers/vmix.ts.
// `path` is what gets appended to /api/vmix/ — `:phaseId` placeholders are filled in by the user.
const VIEWS = [
	{
		name: 'ingameTopBar',
		category: 'Match',
		description: 'Score for the in-game top bar. Respects the "flip players ingame" toggle, so the scores swap sides with the players.',
		variables: ['leftPlayerScore', 'RightPlayerScor'],
	},
	{
		name: 'ingameMap',
		category: 'Match',
		description: 'Current map and match type of the ongoing game.',
		variables: ['mapName', 'matchType'],
	},
	{
		name: 'playerBackgrounds',
		category: 'Match',
		description: 'Map, match type and civ indices for background overlays. The civ values are numeric indices into the civ list, not names.',
		variables: ['mapName', 'matchType', 'leftPlayerCiv', 'rightPlayerCiv'],
	},
	{
		name: 'playerFlags',
		category: 'Match',
		description: 'Numeric flag indices for both players, based on their configured country.',
		variables: ['leftPlayerFlag', 'rightPlayerFlag'],
	},
	{
		name: 'playerVideoLists',
		category: 'Match',
		description: 'Current match player names with their configured vMix video list IDs.',
		variables: ['LeftPlayerName', 'LeftPlayerListId', 'RightPlayerName', 'RightPlayerListId'],
	},
	{
		name: 'currentMatchIntro',
		category: 'Match',
		description: 'Match intro screen: both player names, series scores and pictures.',
		variables: ['leftPlayerName', 'leftPlayerScore', 'LeftPlayerPic', 'rightPlayerName', 'RightPlayerScor', 'RightPlayerPic'],
	},
	{
		name: 'currentMatchIntroDoubleBug',
		category: 'Match',
		description: 'Alternative intro format — round info, a combined "A VS B" string, the current map and the game number.',
		variables: ['roundInfo', 'playerVS', 'currentMap', 'gameNumber'],
	},
	{
		name: 'upcomingMatch',
		category: 'Match',
		description: 'Upcoming match info with the countdown timer (unix timestamp from the match state).',
		variables: ['leftPlayerName', 'LeftPlayerPic', 'rightPlayerName', 'RightPlayerPic', 'timer'],
	},
	{
		name: 'currentMatchHeader',
		category: 'Match',
		description: 'Current match header overlay. `header` is the fixed string "CURRENT MATCH".',
		variables: ['header', 'leftPlayerName', 'leftPlayerScore', 'LeftPlayerPic', 'rightPlayerName', 'RightPlayerScor', 'RightPlayerPic'],
	},
	{
		name: 'currentMatchBand',
		category: 'Match',
		description: 'Current match band overlay. Same as the header view plus `infoText`, which carries the match status text.',
		variables: ['header', 'infoText', 'leftPlayerName', 'leftPlayerScore', 'LeftPlayerPic', 'rightPlayerName', 'RightPlayerScor', 'RightPlayerPic'],
	},
	{
		name: 'playerVsPlayer',
		category: 'Match',
		description: 'Player vs player card. `header` is the round info of the current match.',
		variables: ['header', 'leftPlayerName', 'LeftPlayerPic', 'rightPlayerName', 'RightPlayerPic'],
	},
	{
		name: 'playerVsPlayerStats',
		category: 'Match',
		description:
			'Head-to-head statistics for the current match-up, sourced from the configured H2H provider (Liquipedia, aoe4world or aomstats — selectable in Settings). The `*.Fill.Color` values are hex colours (green/yellow/red) driven by who is ahead — Rectangle for the series record, Rectangle1 for the games record. Up to 3 previous tournaments are returned.',
		variables: [
			'Player1Name',
			'PlayerTL1',
			'PlayerTL2',
			'PlayerTL3',
			'Player1Image',
			'PlayerTR1',
			'PlayerTR2',
			'PlayerTR3',
			'Player2Image',
			'SeriesWins',
			'SeriesLost',
			'SeriesPercent',
			'SeriesWinner',
			'GamesWins',
			'GamesLost',
			'GamesPercent',
			'GamesWinner',
			'Tournament{1-3}Name',
			'Tournament{1-3}Year',
			'Tournament{1-3}Wins',
			'Tournament{1-3}Loss',
			'LeftRectangle.Fill.Color',
			'RightRectangle.Fill.Color',
			'LeftRectangle1.Fill.Color',
			'RightRectangle1.Fill.Color',
		],
	},
	{
		name: 'players',
		category: 'Match',
		description: 'Both players of the current match with flag, team icon, draft picture and role.',
		variables: [
			'leftPlayerName',
			'leftPlayerCountry',
			'leftPlayerTeamPicture',
			'leftPlayerImage',
			'leftPlayerRole',
			'rightPlayerName',
			'rightPlayerCountry',
			'rightPlayerTeamPicture',
			'rightPlayerImage',
			'rightPlayerRole',
		],
	},
	{
		name: 'seriesOver',
		category: 'Match',
		description: 'Whether the current series has concluded, as the string "true" or "false". Bo-series use the win threshold, everything else the total games played.',
		variables: ['seriesOver'],
	},
	{
		name: 'casters',
		category: 'Match',
		description: 'Caster and interviewee info for the current broadcast. The country fields are flagcdn URLs.',
		variables: [
			'Caster1Name',
			'Caster1Country',
			'Caster1Pic',
			'Caster1Team',
			'Caster1Role',
			'Caster2Name',
			'Caster2Country',
			'Caster2Pic',
			'Caster2Team',
			'Caster2Role',
			'IntervieweeName',
			'IntervieweeCountry',
			'IntervieweePic',
			'IntervieweeTeam',
			'IntervieweeRole',
		],
	},
	{
		name: 'todaysMatches',
		category: 'Schedule',
		description:
			'All matches of the active schedule day in a single row. Player fields are numbered continuously (1 and 2 = first match, 3 and 4 = second, and so on); `Schedule{n}` is numbered per match. Unfinished matches return "-" as the score.',
		variables: ['MainTitle', 'Schedule{n}', 'PlayerName{n}', 'PlayerScore{n}', 'PlayerImage{n}'],
	},
	{
		name: 'schedule',
		category: 'Schedule',
		description:
			'One row per match of the active schedule day. `Start` is formatted "Mon DD - HH:MM TZ". The series/game win counts come from the head-to-head data. Unfinished matches return "-" as the score.',
		variables: [
			'Title',
			'Start',
			'LeftPlayerName',
			'LeftPlayerPic',
			'LeftPlayerCountry',
			'LeftPlayerScore',
			'LeftPlayerSeriesWins',
			'LeftPlayerGameWins',
			'RightPlayerName',
			'RightPlayerPic',
			'RightPlayerCountry',
			'RightPlayerScore',
			'RightPlayerSeriesWins',
			'RightPlayerGameWins',
			'BestOf',
			'Finished',
		],
	},
	{
		name: 'scheduleBanner',
		category: 'Schedule',
		description:
			'One row per match of the active schedule day, for the schedule banner. The first unplayed match after a finished one gets `UpNext` = true, and its `Time` reads "UP NEXT" instead of the start time.',
		variables: ['Score', 'LeftPlayer', 'RightPlayer', 'LeftPlayerPic', 'RightPlayerPic', 'BestOf', 'Finished', 'UpNext', 'Time'],
	},
	{
		name: 'nextSeries',
		category: 'Schedule',
		description:
			'The next unplayed series of the active schedule day (falls back to the first one). Includes head-to-head numbers; `*Rectangle.Fill.Color` is coloured on the series record, `*Rectangle1.Fill.Color` on the games record.',
		variables: [
			'DayTitle',
			'Round',
			'Start',
			'LeftPlayerName',
			'LeftPlayerPic',
			'LeftPlayerBracketPic',
			'LeftPlayerCountry',
			'LeftPlayerScore',
			'SeriesWon',
			'GamesWon',
			'RightPlayerName',
			'RightPlayerPic',
			'RightPlayerBracketPic',
			'RightPlayerCountry',
			'RightPlayerScore',
			'SeriesLost',
			'GamesLost',
			'BestOf',
			'Finished',
			'LeftRectangle.Fill.Color',
			'RightRectangle.Fill.Color',
			'LeftRectangle1.Fill.Color',
			'RightRectangle1.Fill.Color',
		],
	},
	{
		name: 'startggBracket',
		category: 'Bracket',
		description:
			'SE/DE bracket data from start.gg for one phase. Sets follow the configured set sort order and every slot becomes a numbered player. Forfeit scores display as "FF", empty slots as "-".',
		path: 'startggBracket/:phaseId',
		params: ['length', 'outputWinner'],
		variables: ['PlayerImage{n}', 'PlayerName{n}', 'PlayerScore{n}', 'PlayerTeam{n}'],
	},
	{
		name: 'startggBracketCombined',
		category: 'Bracket',
		description: 'Same as startggBracket, but merges several phases into one flat row. Pass the phase IDs comma-separated; the indices continue across phases.',
		path: 'startggBracketCombined/:phaseId,:phaseId',
		params: ['length', 'outputWinner'],
		variables: ['PlayerImage{n}', 'PlayerName{n}', 'PlayerScore{n}', 'PlayerTeam{n}'],
	},
	{
		name: 'startggRoundRobinStandings',
		category: 'Bracket',
		description: 'Round-robin standings from start.gg, one row per phase group. Without `sort` the start.gg placement order is used.',
		path: 'startggRoundRobinStandings/:phaseId',
		params: ['sort'],
		variables: [
			'PlayerName{n}',
			'PlayerImage{n}',
			'PlayerTeamImage{n}',
			'PlayerSeries{n}',
			'PlayerWins{n}',
			'PlayerLosses{n}',
			'PlayerGames{n}',
			'PlayerGameWins{n}',
			'PlayerGameLosses{n}',
			'PlayerGD{n}',
		],
	},
	{
		name: 'startggRoundRobinGrid',
		category: 'Bracket',
		description:
			'Round-robin head-to-head grid, one row per phase group. `Score{n}` is a grid cell in row-major order skipping the diagonal, formatted "losses - wins" against that opponent ("FF"/"W" on forfeits). `Background{n}` marks whether that cell has a result.',
		path: 'startggRoundRobinGrid/:phaseId',
		params: ['sort'],
		variables: [
			'PlayerName{n}',
			'PlayerNameC{n}',
			'PlayerImage{n}',
			'PlayerTeamImage{n}',
			'PlayerSeries{n}',
			'TotalSeriesWins{n}',
			'TotalSeriesLosses{n}',
			'PlayerGames{n}',
			'TotalGameWins{n}',
			'TotalGameLosses{n}',
			'Score{n}',
			'Background{n}',
		],
	},
	{
		name: 'gslGroups',
		category: 'Bracket',
		description:
			'GSL group (4-player double elimination) from start.gg, one row per group. Standings 1-4 are sorted by series wins, losses, game difference, games won, head-to-head and seed; pass `sort=seed` for seed order instead. Each match block numbers its slots from 1 (OpeningMatch holds both opening sets, so 4 slots; the others 2). Forfeits count as a 3-0.',
		path: 'gslGroups/:phaseId',
		params: ['sort'],
		variables: [
			'PlayerImage{1-4}',
			'PlayerName{1-4}',
			'SeriesWL{1-4}',
			'GamesWL{1-4}',
			'GameDiff{1-4}',
			'OpeningMatchP{1-4}',
			'OpeningMatchS{1-4}',
			'OpeningMatchI{1-4}',
			'WinnerP{1-2}',
			'WinnerS{1-2}',
			'WinnerI{1-2}',
			'EliminationP{1-2}',
			'EliminationS{1-2}',
			'EliminationI{1-2}',
			'DeciderP{1-2}',
			'DeciderS{1-2}',
			'DeciderI{1-2}',
		],
	},
	{
		name: 'onimaruStandings',
		category: 'Bracket',
		description:
			'Full standings of an Onimaru-provider tournament in a single row: per-player totals, each player round-by-round result, and every match per round. `sort=wins` sorts by series wins then game win-rate.',
		params: ['sort'],
		variables: [
			'PlayerName{n}',
			'PlayerImage{n}',
			'PlayerTeamImage{n}',
			'PlayerSeed{n}',
			'PlayerRank{n}',
			'PlayerSeries{n}',
			'PlayerWins{n}',
			'PlayerLosses{n}',
			'PlayerMatchesPlayed{n}',
			'PlayerMatchRecord{n}',
			'PlayerGames{n}',
			'PlayerGameWins{n}',
			'PlayerGameLosses{n}',
			'PlayerGameRecord{n}',
			'PlayerGD{n}',
			'PlayerPoints{n}',
			'PlayerBuchholz{n}',
			'PlayerCountry{n}',
			'Player{n}_R{r}_Opponent',
			'Player{n}_R{r}_Score',
			'Player{n}_R{r}_Result',
			'R{r}M{m}_Player1',
			'R{r}M{m}_Player2',
			'R{r}M{m}_Score1',
			'R{r}M{m}_Score2',
			'R{r}M{m}_Score',
			'R{r}M{m}_Winner',
			'R{r}M{m}_Status',
		],
	},
	{
		name: 'onimaruSwissStandings',
		category: 'Bracket',
		description:
			'Swiss standings of an Onimaru-provider tournament in a single row. Every player gets 3 round slots numbered with one flat counter across all players (player 1 = Opponent1-3, player 2 = Opponent4-6, and so on). `sort=wins` sorts by series wins then game win-rate.',
		params: ['sort'],
		variables: ['PlayerImage{n}', 'PlayerName{n}', 'PlayerFlag{n}', 'Matches{n}', 'Games{n}', 'GD{n}', 'Points{n}', 'Opponent{n}', 'Score{n}'],
	},
	{
		name: 'onimaruPlayoffs',
		category: 'Bracket',
		description:
			'Playoff bracket of an Onimaru-provider tournament in a single row: 4 quarterfinals, 2 semifinals and the final are always emitted (empty slots become "TBD" with a placeholder picture), so the indices run 1-14 in bracket order.',
		variables: ['PlayerName{1-14}', 'PlayerImage{1-14}', 'PlayerScore{1-14}'],
	},
	{
		name: 'civStats',
		category: 'Stats',
		description: 'Tournament civ pick/ban/win statistics from the aoe4world data, plus the most-played match-up (the MP* fields).',
		variables: [
			'MPCiv1',
			'MPCivName1',
			'MPCiv2',
			'MPCivName2',
			'MPRate',
			'CivName{n}',
			'CivImage{n}',
			'Picked{n}',
			'Wins{n}',
			'Losses{n}',
			'Bans{n}',
			'Winrate{n}',
			'FirstPicks{n}',
			'Sniped{n}',
		],
	},
]

const PARAM_DOCS = {
	length: 'Integer. Only keep the last N sets of the bracket.',
	outputWinner: 'Integer (or `true` = 1). Append that many winners of the final round, e.g. 2 for the top-4 qualifiers.',
	sort: 'Sort order of the players. `wins` sorts by record instead of the provider order; gslGroups also accepts `seed`.',
}

const CATEGORIES = ['All', 'Match', 'Schedule', 'Bracket', 'Stats']

function ViewCard({ view, baseUrl }) {
	const [copied, setCopied] = useState(false)
	const url = `${baseUrl}/api/vmix/${view.path ?? view.name}`

	function copyUrl() {
		navigator.clipboard.writeText(url).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		})
	}

	return (
		<div className="border border-gray-400 rounded p-4 mb-4">
			<div className="flex flex-row items-center gap-3 flex-wrap">
				<h2 className="text-xl font-bold">{view.name}</h2>
				<span className="text-xs px-2 py-0.5 rounded bg-gray-500 text-white">{view.category}</span>
			</div>

			<p className="my-2 text-gray-900">{view.description}</p>

			<div className="flex flex-row items-center gap-2 flex-wrap my-2">
				<code className="px-2 py-1 bg-gray-200 text-[#181818] rounded text-sm break-all">{url}</code>
				<button className="bg-teal-700 hover:bg-teal-800 text-white text-sm px-2 py-1 rounded" onClick={copyUrl}>
					{copied ? 'Copied!' : 'Copy URL'}
				</button>
			</div>

			{view.path && <p className="text-sm text-gray-500 my-1">Replace :phaseId with the start.gg phase ID.</p>}

			{view.params && (
				<div className="my-2">
					<h3 className="font-semibold">Query parameters</h3>
					<ul className="list-disc ml-6 text-sm">
						{view.params.map((param) => (
							<li key={param} className="text-gray-900">
								<code>{param}</code> — {PARAM_DOCS[param]}
							</li>
						))}
					</ul>
				</div>
			)}

			<div className="my-2">
				<h3 className="font-semibold">
					Output variables <span className="text-sm text-gray-500">({view.variables.length})</span>
				</h3>
				<div className="flex flex-row flex-wrap gap-1 mt-1">
					{view.variables.map((variable) => (
						<code key={variable} className="px-2 py-0.5 bg-gray-200 text-[#181818] rounded text-sm">
							{variable}
						</code>
					))}
				</div>
			</div>
		</div>
	)
}

export default function VmixDocs() {
	const [theme, setTheme] = useState(() => {
		return localStorage.getItem('theme') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
	})
	const [search, setSearch] = useState('')
	const [category, setCategory] = useState('All')

	const baseUrl = getBackendUrl() || window.location.origin

	const filtered = useMemo(() => {
		const needle = search.trim().toLowerCase()
		return VIEWS.filter((view) => {
			if (category !== 'All' && view.category !== category) return false
			if (!needle) return true
			return (
				view.name.toLowerCase().includes(needle) ||
				view.description.toLowerCase().includes(needle) ||
				view.variables.some((variable) => variable.toLowerCase().includes(needle))
			)
		})
	}, [search, category])

	return (
		<div className={`p-4 dashboard ${theme}`}>
			<div className="flex flex-row justify-between items-start gap-4 flex-wrap">
				<div>
					<h1 className="text-3xl font-bold">vMix Data Sources</h1>
					<p className="text-gray-900 mt-1">
						Every view served by <code>GET /api/vmix/:viewName/:id?</code>. No auth required — point a vMix JSON data source at the URL and it returns an array of
						key/value rows. Missing values come back as empty strings, and `{'{n}'}` marks a field that repeats per player, set or civ.
					</p>
					<p className="text-sm text-gray-500 mt-1">
						Base URL: <code>{baseUrl}</code>
					</p>
				</div>

				<div className="flex flex-row items-center gap-3">
					<Link to="/Dashboard">
						<button className="rounded">Back to Dashboard</button>
					</Link>
					<div className="cursor-pointer" onClick={() => setTheme((curr) => (curr === 'light' ? 'dark' : 'light'))}>
						{theme === 'light' ? '🌞' : '🌚'}
					</div>
				</div>
			</div>

			<div className="flex flex-row items-center gap-2 my-4 flex-wrap">
				<input
					className="border border-gray-400 rounded px-2 py-1 w-72 text-left text-[#181818]"
					placeholder="Search view, description or variable"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				{CATEGORIES.map((cat) => (
					<button
						key={cat}
						className={`rounded text-sm px-2 py-1 ${category === cat ? 'bg-teal-700 hover:bg-teal-800 text-white' : ''}`}
						onClick={() => setCategory(cat)}>
						{cat}
					</button>
				))}
				<span className="text-sm text-gray-500">
					{filtered.length} of {VIEWS.length} views
				</span>
			</div>

			{filtered.map((view) => (
				<ViewCard key={view.name} view={view} baseUrl={baseUrl} />
			))}

			{filtered.length === 0 && <p className="text-gray-500">No views match that search.</p>}
		</div>
	)
}
